import { useCallback, useMemo } from "react";
import { Box, CircularProgress, Tooltip } from "@mui/material";

import {
  useResolvedRateSchedules,
  useUpdateRate,
  useUpdateProposalRate,
} from "../../../hooks/useRates";
import {
  coverageTierToLabelLong,
  formatRateValue,
  formatRateValueForDisplay,
  sortRatesByCoverageTier,
} from "../../../utils/rateFormatters";
import {
  currentSchedules,
  displayedRenewalSchedule,
  ratesByTier,
} from "../../../utils/rateSets";
import { isPlanTotalRepeatedPerTier } from "../../../utils/schedule-helpers";
import { buildRateFieldPath } from "../../../utils/rateFieldPath";

import { EnrollmentFlag } from "./EnrollmentFlag";
import { DebouncedNumericInput } from "./NumericInput";
import { RateReviewCell } from "./RateReviewCell";
import {
  FONT_SIZE,
  AG_BORDER,
  AG_HEADER_BG_COLOR,
  inputSx,
  gridRowSx,
  gridCellSx,
  headerCellSx,
} from "./constants";

import type { AxiosInstance } from "axios";
import type { ColDef } from "ag-grid-community";
import type { FC } from "react";
import type { BenefitPlan, BenefitPlanGroup } from "../../../types/models";
import type { Rate, RateSchedule } from "../../../types";

export interface CompositeRendererProps {
  plan: BenefitPlan;
  axiosInstance?: AxiosInstance;
  /** Coverage type (e.g., dental, vision, std) for LOC-specific behavior */
  coverageType?: string;
  onRateSaved: () => void;
  readOnly?: boolean;
}

/**
 * Build column definitions for composite (tiered) rate design in RatesGrid
 * Creates columns for each coverage tier
 */
export const buildCompositeColumns = (_group: BenefitPlanGroup): ColDef[] =>
  // TODO: Extract unique coverage tiers from rate schedules
  // and create a column for each tier
  [];

/**
 * Build column definitions for composite rate design in SummaryGrid
 * Returns array of ColDef for sub-columns within a benefit plan
 */
export const buildCompositeSummaryColumns = (
  _planId: string,
  _planName: string,
): ColDef[] =>
  // TODO: Implement composite summary column generation
  [];

// CSS Grid container styling - 3 columns: Tier (fixed), Enrolled Lives (flex), Rate (flex)
// Uses flexible fr units to expand to full column width
const gridContainerSx = {
  display: "grid",
  gridTemplateColumns: "minmax(120px, 1fr) minmax(80px, 1fr) minmax(80px, 1fr)",
  width: "100%",
};

/**
 * Renderer for composite (tiered) rate design
 *
 * Displays rates by coverage tier per benefit plan in the group.
 * Each tier has its own row with numberOfLives and rate inputs.
 *
 * Layout:
 * | Rate Structure |                | Tiered (X tiers) |
 * | # of tiers     | Enrolled Lives | Rate             |
 * | Employee Only  | [input]        | [input]          |
 * | EE & Spouse    | [input]        | [input]          |
 * ...
 */
export const CompositeRenderer: FC<CompositeRendererProps> = ({
  plan,
  axiosInstance,
  onRateSaved,
  readOnly,
}) => {
  const { data: rateSchedules, isLoading } = useResolvedRateSchedules(
    plan,
    axiosInstance,
  );

  const rateSchedule: RateSchedule | undefined =
    currentSchedules(rateSchedules)[0];
  const rates: Rate[] = useMemo(
    () => sortRatesByCoverageTier(rateSchedule?.rates || []),
    [rateSchedule?.rates],
  );

  // A plan-wide total repeated on every rate row is not a per-tier breakdown, so it
  // belongs on the schedule header rather than in each tier's cell.
  const planTotalLives =
    rateSchedule && isPlanTotalRepeatedPerTier(rateSchedule)
      ? rateSchedule.numberOfLives
      : undefined;

  // Renewal / revised-renewal display for the current plan. The in-grid column
  // shows revised renewal when present, else renewal (AC precedence); the tooltip
  // surfaces every populated set. Absent for carrier plans, which have no renewal.
  const renewalInfo = displayedRenewalSchedule(rateSchedules);
  const showRenewalColumn = renewalInfo !== undefined;
  const renewalColumnLabel =
    renewalInfo?.category === "revisedRenewal" ? "Rev. Ren." : "Renewal";
  const columnByTier = ratesByTier(renewalInfo?.schedule);
  const currentByTier = ratesByTier(rateSchedule);
  const renewalByTier = ratesByTier(
    (rateSchedules ?? []).find(
      (schedule) => schedule.rateCategory === "renewal",
    ),
  );
  const revisedByTier = ratesByTier(
    (rateSchedules ?? []).find(
      (schedule) => schedule.rateCategory === "revisedRenewal",
    ),
  );

  // Mutation hook for updating rates
  const updateRateMutation = useUpdateRate(axiosInstance!);
  const updateProposalRateMutation = useUpdateProposalRate(axiosInstance!);
  const rateMutation =
    plan.rateScheduleSource === "proposal"
      ? updateProposalRateMutation
      : updateRateMutation;

  // Handle commit to save changes for numberOfLives
  const handleLivesCommit = useCallback(
    (rate: Rate, value: string) => {
      if (!axiosInstance || !rateSchedule?.id || !rate.id) return;

      const numericValue = parseInt(value, 10);
      if (isNaN(numericValue) || numericValue === rate.numberOfLives) return;

      rateMutation.mutate(
        {
          scheduleId: rateSchedule.id,
          rateId: rate.id,
          benefitPlanId: plan.id,
          rate: { numberOfLives: numericValue },
        },
        {
          onSuccess: () => onRateSaved(),
        },
      );
    },
    [axiosInstance, onRateSaved, rateMutation, rateSchedule?.id, plan.id],
  );

  // Handle commit to save changes for rate
  const handleRateCommit = useCallback(
    (rate: Rate, value: string) => {
      if (!axiosInstance || !rateSchedule?.id || !rate.id) return;

      const numericValue = parseFloat(value);
      if (isNaN(numericValue) || numericValue === rate.rate) return;

      rateMutation.mutate(
        {
          scheduleId: rateSchedule.id,
          rateId: rate.id,
          benefitPlanId: plan.id,
          rate: { rate: numericValue },
        },
        {
          onSuccess: () => onRateSaved(),
        },
      );
    },
    [axiosInstance, onRateSaved, rateMutation, rateSchedule?.id, plan.id],
  );

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          py: 2,
        }}
      >
        <CircularProgress size={20} />
      </Box>
    );
  }

  if (!rateSchedule) {
    return (
      <Box sx={{ p: 1, fontSize: "13px", color: "text.secondary" }}>
        No rate schedule
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        position: "relative",
        zIndex: 1,
      }}
    >
      {/* Header Row - Rate Design Label */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          height: "36px",
          backgroundColor: AG_HEADER_BG_COLOR,
          borderBottom: AG_BORDER,
          px: 1,
        }}
      >
        <Box sx={{ fontWeight: 600, fontSize: FONT_SIZE }}>
          Tiered ({rates.length} tiers)
        </Box>
        {planTotalLives != null && (
          <Tooltip title="The carrier reported one enrolled-lives total for the plan rather than a count per tier.">
            <Box sx={{ fontSize: FONT_SIZE }}>
              {planTotalLives} enrolled (plan total)
            </Box>
          </Tooltip>
        )}
      </Box>

      <Box
        sx={{
          ...gridContainerSx,
          // With the renewal column the four tracks overflow the pinned benefit-plan cell at
          // the default (medium) column width, so give each a min wide enough to hold a rate
          // value on one line and scroll horizontally within the cell rather than truncating.
          ...(showRenewalColumn
            ? {
                gridTemplateColumns:
                  "minmax(120px, 1fr) repeat(3, minmax(84px, 1fr))",
                overflowX: "auto",
              }
            : {}),
        }}
      >
        {/* Sub-header Row - Column Labels */}
        <Box sx={gridRowSx}>
          <Box sx={{ ...headerCellSx }}># of tiers</Box>
          <Box sx={{ ...headerCellSx }}>Enrolled Lives</Box>
          <Box
            sx={{
              ...headerCellSx,
              borderRight: showRenewalColumn ? undefined : "none",
            }}
          >
            {showRenewalColumn ? "Current" : "Rate"}
          </Box>
          {showRenewalColumn && (
            <Box
              sx={{
                ...headerCellSx,
                borderRight: "none",
                borderLeft: AG_BORDER,
              }}
            >
              {renewalColumnLabel}
            </Box>
          )}
        </Box>

        {/* Rate Rows - One per coverage tier */}
        {rates.map((rate, rowIndex) => {
          const isLastRow = rowIndex === rates.length - 1;
          const tierLabel = coverageTierToLabelLong(
            rate.coverageTierCode,
            rates.length,
          );
          // 2 columns per row: Enrolled Lives, Rate
          const baseCellIndex = rowIndex * 2;
          const tierKey = rate.coverageTierCode ?? "";
          return (
            <Box key={rate.id} sx={gridRowSx}>
              {/* Tier Label */}
              <Box
                sx={{
                  ...headerCellSx,
                  borderBottom: isLastRow ? "none" : AG_BORDER,
                }}
              >
                {tierLabel}
              </Box>
              {/* Enrolled Lives Input */}
              <RateReviewCell
                planId={plan.id}
                fieldPath={buildRateFieldPath(
                  rateSchedule,
                  rate,
                  "numberOfLives",
                )}
                sx={{
                  ...gridCellSx,
                  borderBottom: isLastRow ? "none" : AG_BORDER,
                }}
              >
                <EnrollmentFlag rate={rate} measure="lives">
                  <DebouncedNumericInput
                    serverValue={
                      rate.numberOfLives != null && planTotalLives == null
                        ? String(rate.numberOfLives)
                        : ""
                    }
                    onCommit={(value) => handleLivesCommit(rate, value)}
                    allowDecimals={false}
                    sx={inputSx}
                    gridId={`composite-${plan.id}`}
                    cellIndex={baseCellIndex}
                    columnsInRow={2}
                    readOnly={readOnly}
                    inputProps={{
                      "aria-label": `${tierLabel} Enrolled Lives`,
                    }}
                  />
                </EnrollmentFlag>
              </RateReviewCell>
              {/* Rate Input */}
              <RateReviewCell
                planId={plan.id}
                fieldPath={buildRateFieldPath(rateSchedule, rate, "rate")}
                sx={{
                  ...gridCellSx,
                  borderRight: showRenewalColumn ? undefined : "none",
                  borderBottom: isLastRow ? "none" : AG_BORDER,
                }}
              >
                <DebouncedNumericInput
                  serverValue={formatRateValue(rate.rate, rates[0]?.unit)}
                  onCommit={(value) => handleRateCommit(rate, value)}
                  allowDecimals
                  isCurrency
                  sx={inputSx}
                  gridId={`composite-${plan.id}`}
                  cellIndex={baseCellIndex + 1}
                  columnsInRow={2}
                  readOnly={readOnly}
                  inputProps={{
                    "aria-label": `${tierLabel} Rate`,
                  }}
                />
              </RateReviewCell>
              {/* Renewal / Revised Renewal (read-only; tooltip shows all sets) */}
              {showRenewalColumn && (
                <Tooltip
                  arrow
                  title={
                    <Box sx={{ fontSize: FONT_SIZE }}>
                      <Box>
                        Current:{" "}
                        {formatRateValueForDisplay(
                          currentByTier[tierKey],
                          rates[0]?.unit,
                        )}
                      </Box>
                      {renewalByTier[tierKey] != null && (
                        <Box>
                          Renewal:{" "}
                          {formatRateValueForDisplay(
                            renewalByTier[tierKey],
                            rates[0]?.unit,
                          )}
                        </Box>
                      )}
                      {revisedByTier[tierKey] != null && (
                        <Box>
                          Revised Renewal:{" "}
                          {formatRateValueForDisplay(
                            revisedByTier[tierKey],
                            rates[0]?.unit,
                          )}
                        </Box>
                      )}
                    </Box>
                  }
                >
                  <Box
                    tabIndex={0}
                    sx={{
                      ...gridCellSx,
                      borderRight: "none",
                      borderLeft: AG_BORDER,
                      borderBottom: isLastRow ? "none" : AG_BORDER,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "flex-end",
                      px: 1,
                      fontSize: FONT_SIZE,
                      // Read-only, but match the editable cells' hover shade and let
                      // the analyst select/copy the value (text cursor + selection).
                      cursor: "text",
                      userSelect: "text",
                      "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)" },
                    }}
                    aria-label={`${tierLabel} ${renewalColumnLabel}`}
                  >
                    {formatRateValueForDisplay(
                      columnByTier[tierKey],
                      rates[0]?.unit,
                    )}
                  </Box>
                </Tooltip>
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};
